/**
 * Click-time rewrite for assistant links.
 * Shortcuts and legacy paths must match
 * services/api/src/routes/portal/admin/assistant/indexing/portalPaths.ts.
 * When a live route table is passed, menu paths from the portal router win.
 */
import type { AssistantRouteTable } from './assistantRoutes'

const ALIASES: Record<string, string> = {
  '/dashboard': '/admin',
  '/leads': '/admin/lead-management',
  '/lead-management': '/admin/lead-management',
  '/applications': '/admin/application-management',
  '/financing': '/admin/financing-profiles',
  '/inventory': '/admin/inventory-v3',
  '/admin/inventory': '/admin/inventory-v3',
  '/shield': '/admin/shield-inventory-v3',
  '/admin/shield-inventory-v2': '/admin/shield-inventory-v3',
  '/importer': '/admin/inventory-importer',
  '/photos': '/admin/management/photo-upload',
  '/transporter': '/admin/management/transporter-management',
  '/parts': '/admin/management/parts-management',
  '/locations': '/admin/location-management',
  '/terminals': '/admin/terminal-management',
  '/users': '/admin/user-management-v2',
  '/admin/user-management': '/admin/user-management-v2',
  '/expenses': '/admin/expense-management',
  '/invoices': '/admin/invoice-management',
  '/time': '/admin/time-tracker',
  '/deals': '/admin/deal-approval',
  '/titles': '/admin/title-manager',
  '/submissions': '/admin/management/submission-management',
  '/business-submissions': '/admin/business-submissions',
  '/lists': '/admin/list-builder',
  '/my-lists': '/admin/my-inventory-lists',
  '/graybook': '/optic-gray-book-calculator',
  '/pricing': '/admin/tools/pricing-models',
  '/pricing-compare': '/admin/tools/pricing-models/compare',
  '/shipment': '/admin/tools/shipment-calculator',
  '/vin': '/admin/tools/vin-decoder',
  '/vin-decoder': '/admin/tools/vin-decoder',
  '/quick-leads': '/admin/tools/quick-leads',
  '/audit': '/admin/tools/audit-log',
  '/messages': '/admin/tools/messages',
  '/ltv': '/optic-ltv-calculator',
  '/reviews': '/admin/performance-reviews',
  '/performance': '/admin/performance-reviews',
  '/client-activity': '/admin/client-activity',
}

const KNOWN_PATHS = new Set<string>([
  ...Object.values(ALIASES),
  '/admin',
  '/fleet-list',
  '/notification-center',
  '/account-settings',
  '/support-request',
])

const DYNAMIC_PATHS: RegExp[] = [
  /^\/admin\/lead-management\/contacts\/[^/]+$/,
  /^\/admin\/lead-management\/companies\/[^/]+$/,
  /^\/admin\/financing-profiles\/client\/[^/]+$/,
  /^\/admin\/financing-profiles\/company\/[^/]+$/,
  /^\/admin\/financing-profiles\/company\/[^/]+\/client\/[^/]+$/,
  /^\/admin\/terminal-management\/[^/]+$/,
  /^\/admin\/tools\/pricing-models\/[^/]+$/,
  /^\/client(?:\/.*)?$/,
  /^\/bankAgent(?:\/.*)?$/,
]

function isLivePath(pathname: string): boolean {
  if (KNOWN_PATHS.has(pathname)) return true
  if (pathname === '/admin/tools/pricing-models/compare') return true
  return DYNAMIC_PATHS.some(pattern => pattern.test(pathname))
}

/** Longest first so /vin-decoder wins over /vin. */
export const ASSISTANT_LINK_ALIASES = Object.keys(ALIASES).sort(
  (left, right) => right.length - left.length,
)

const PORTAL_HOST = /(^|\.)optictruckworks\.com$|^localhost$|^127\.0\.0\.1$/

function splitLocation(value: string): { pathname: string; suffix: string } {
  const hashAt = value.indexOf('#')
  const queryAt = value.indexOf('?')
  const cuts = [hashAt, queryAt].filter(index => index >= 0)
  const cut = cuts.length > 0 ? Math.min(...cuts) : value.length
  return { pathname: value.slice(0, cut), suffix: value.slice(cut) }
}

function isAllowedPath(pathname: string, routes?: AssistantRouteTable | null): boolean {
  if (!routes) return isLivePath(pathname)
  if (routes.paths.has(pathname)) return true
  return routes.detailPatterns.some(pattern => pattern.test(pathname))
}

/** Map a chat link to a live route, or null when it should not navigate. */
export function resolveAssistantPath(
  raw: string,
  routes?: AssistantRouteTable | null,
): string | null {
  let value = raw.trim()
  if (!value) return null

  if (value.startsWith('http://') || value.startsWith('https://')) {
    try {
      const url = new URL(value)
      if (!PORTAL_HOST.test(url.hostname)) return null
      value = `${url.pathname}${url.search}${url.hash}`
    } catch {
      return null
    }
  }

  value = value.replace(/^[`'"]+/, '').replace(/[`'".;,!?]+$/, '')
  if (!value.startsWith('/')) value = `/${value}`

  const { pathname, suffix } = splitLocation(value)
  let normalized = pathname.replace(/\/+$/, '') || '/'

  const inventoryDetail = normalized.match(/^\/admin\/inventory\/([^/]+)$/)
  if (inventoryDetail?.[1]) {
    return `/admin/inventory-v3?open=edit&truck=${encodeURIComponent(decodeURIComponent(inventoryDetail[1]))}`
  }

  normalized = routes?.aliases[normalized] ?? ALIASES[normalized] ?? normalized
  if (!isAllowedPath(normalized, routes)) return null
  return `${normalized}${suffix}`
}

export const ASSISTANT_QUERY = 'assistant'

/** Keep or drop the assistant query without touching the destination's own search. */
export function withAssistantQuery(search: string, open: boolean): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  if (open) params.set(ASSISTANT_QUERY, '1')
  else params.delete(ASSISTANT_QUERY)
  const next = params.toString()
  return next ? `?${next}` : ''
}

/** Split a resolved path so React Router keeps the query and hash. */
export function assistantPathToLocation(path: string): {
  pathname: string
  search: string
  hash: string
} {
  const hashAt = path.indexOf('#')
  const queryAt = path.indexOf('?')
  let pathname = path
  let search = ''
  let hash = ''

  if (queryAt >= 0 && (hashAt < 0 || queryAt < hashAt)) {
    pathname = path.slice(0, queryAt)
    const rest = path.slice(queryAt)
    const hashInRest = rest.indexOf('#')
    if (hashInRest >= 0) {
      search = rest.slice(0, hashInRest)
      hash = rest.slice(hashInRest)
    } else {
      search = rest
    }
  } else if (hashAt >= 0) {
    pathname = path.slice(0, hashAt)
    hash = path.slice(hashAt)
  }

  return { pathname, search, hash }
}
